import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import en from '@angular/common/locales/en';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';

import { appRoutingProviders } from './app.routes';
import { authInterceptor } from '@common/interceptors/auth.interceptor';
import { securityInterceptor } from '@common/interceptors/security.interceptor';
import { SecurityService } from '@common/services/security.service';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { LANGS } from '@common/langs';
import { getLang } from '@common/services/i18n.service';

registerLocaleData(en);

/**
 * Factory function to initialize security features when the application starts
 */
function initializeSecurityFactory(securityService: SecurityService): () => void {
  return (): void => {
    securityService.initialize();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    appRoutingProviders,
    provideNzI18n(en_US),
    importProvidersFrom(FormsModule),
    provideAnimationsAsync(),
    // Register security initializer
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSecurityFactory,
      deps: [SecurityService],
      multi: true,
    },
    // Register HTTP interceptors - order matters, security should be first
    provideHttpClient(withInterceptors([securityInterceptor, authInterceptor])),
    provideTransloco({
      config: {
        availableLangs: LANGS,
        defaultLang: getLang(),
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        fallbackLang: 'en',
        missingHandler: {
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
